export type BindingAction =
  | 'throttleUp'
  | 'throttleDown'
  | 'throttleCut'
  | 'strafeLeft'
  | 'strafeRight'
  | 'verticalUp'
  | 'verticalDown'
  | 'rollLeft'
  | 'rollRight'
  | 'yawLeft'
  | 'yawRight'
  | 'pitchUp'
  | 'pitchDown'
  | 'toggleAssist'
  | 'toggleRotAssist'
  | 'boost'
  | 'fire'
  | 'toggleCamera'
  | 'toggleTargetMenu'
  | 'targetSignal'
  | 'toggleSupercruise'
  | 'dockRequest'
  | 'scoop'
  | 'verify'
  | 'toggleOrbitLines';

export type BindingMap = Record<BindingAction, string>;

export interface BindingDescriptor {
  action: BindingAction;
  category: string;
  label: string;
}

export const BINDING_ORDER: readonly BindingDescriptor[] = [
  { action: 'throttleUp', category: 'Flight', label: 'Throttle Up' },
  { action: 'throttleDown', category: 'Flight', label: 'Throttle Down' },
  { action: 'throttleCut', category: 'Flight', label: 'Cut Throttle' },
  { action: 'strafeLeft', category: 'Flight', label: 'Strafe Left' },
  { action: 'strafeRight', category: 'Flight', label: 'Strafe Right' },
  { action: 'verticalUp', category: 'Flight', label: 'Vertical Up' },
  { action: 'verticalDown', category: 'Flight', label: 'Vertical Down' },
  { action: 'rollLeft', category: 'Flight', label: 'Roll Left' },
  { action: 'rollRight', category: 'Flight', label: 'Roll Right' },
  { action: 'yawLeft', category: 'Flight', label: 'Yaw Left' },
  { action: 'yawRight', category: 'Flight', label: 'Yaw Right' },
  { action: 'pitchUp', category: 'Flight', label: 'Pitch Up' },
  { action: 'pitchDown', category: 'Flight', label: 'Pitch Down' },
  { action: 'toggleAssist', category: 'Systems', label: 'Assist' },
  { action: 'toggleRotAssist', category: 'Systems', label: 'Rot Assist' },
  { action: 'boost', category: 'Systems', label: 'Boost' },
  { action: 'fire', category: 'Systems', label: 'Fire' },
  { action: 'toggleCamera', category: 'Systems', label: 'Camera' },
  { action: 'toggleTargetMenu', category: 'Systems', label: 'Target Menu' },
  { action: 'targetSignal', category: 'Systems', label: 'Target Signal' },
  { action: 'toggleSupercruise', category: 'Systems', label: 'Supercruise' },
  { action: 'dockRequest', category: 'Systems', label: 'Dock' },
  { action: 'scoop', category: 'Systems', label: 'Scoop' },
  { action: 'verify', category: 'Systems', label: 'Verify' },
  { action: 'toggleOrbitLines', category: 'Systems', label: 'Orbit Lines' },
];

export const DEFAULT_KEYMAP: BindingMap = {
  throttleUp: 'w',
  throttleDown: 's',
  throttleCut: 'z',
  strafeLeft: 'a',
  strafeRight: 'd',
  verticalUp: 'Space',
  verticalDown: 'Control',
  rollLeft: 'q',
  rollRight: 'e',
  yawLeft: 'ArrowLeft',
  yawRight: 'ArrowRight',
  pitchUp: 'ArrowUp',
  pitchDown: 'ArrowDown',
  toggleAssist: 'f',
  toggleRotAssist: 'v',
  boost: 'Shift',
  fire: 'r',
  toggleCamera: 'c',
  toggleTargetMenu: 'Tab',
  targetSignal: 'n',
  toggleSupercruise: 'x',
  dockRequest: 'g',
  scoop: 't',
  verify: 'v',
  toggleOrbitLines: 'o',
};

const KEY_NAMES: Record<string, string> = {
  ArrowDown: 'DOWN',
  ArrowLeft: 'LEFT',
  ArrowRight: 'RIGHT',
  ArrowUp: 'UP',
  Backspace: 'BKSP',
  Control: 'CTRL',
  Escape: 'ESC',
  Shift: 'SHIFT',
  Space: 'SPACE',
  Tab: 'TAB',
};

export const normalizeBindingKey = (value: string): string => {
  if (value === ' ') return 'Space';
  if (value.length === 1) return value.toLowerCase();
  return value;
};

export const displayBindingKey = (value: string): string =>
  KEY_NAMES[value] ?? value.toUpperCase();

export const actionsForKey = (key: string, keymap: BindingMap): BindingAction[] => {
  const matches: BindingAction[] = [];
  for (const action of Object.keys(keymap) as BindingAction[]) {
    if (keymap[action] === key) matches.push(action);
  }
  return matches;
};
